import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../../components';
import logo from '../../assets/klyx-logo1.png';
import onChainVsHostedSvg from '../../assets/diagrams/on-chain-vs-hosted.svg';
import { useAppDispatch, useAppSelector } from '@/redux/Store';
import { fetchEarnConfig } from '@/redux/thunks/achievementThunks';
import { selectEarnConfig } from '@/redux/selectors/achievementSelectors';
import { sumFixedCatalogKlv } from '@/utils/achievementEarn';
import './About.scss';

const AboutPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const earnConfig = useAppSelector(selectEarnConfig);

  // Land at the top on navigation in — same pattern as the post
  // detail page (PR #407). React Router doesn't reset window scroll
  // on route change, so clicking Learn more from a scrolled-down
  // /home landed the reader mid-page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, []);

  // Powers the "earn up to N KLV" dynamic callout. Cheap single call,
  // cached in Redux across pages (shared with /achievements + /how-it-works).
  useEffect(() => {
    if (!earnConfig) dispatch(fetchEarnConfig());
  }, [dispatch, earnConfig]);

  const catalogTotalKlv = Math.round(sumFixedCatalogKlv(earnConfig));

  return (
    <div className="about-page">
      <div className="about-container">
        {/* 3.1 Hero — mirrors the home hero voice (concrete creator
            benefits, no crypto vocabulary above the fold). Refactor
            doc §3.1. */}
        <header className="about-header">
          <img src={logo} alt="Klyx" className="about-header__logo" />
          <h1>What is Klyx?</h1>
          <p className="about-header__tagline">
            Klyx gives creators a profile they actually own — not one rented
            from a platform. Your identity, followers, and reputation belong
            to you. No app can revoke them. No algorithm decides who gets to
            see you.
          </p>
        </header>

        {/* 3.2 Why we're building this — two paragraphs, plain
            English, cribbed from v0.3 "anchor belief" section with
            crypto vocabulary stripped. Refactor doc §3.2. */}
        <section className="about-section">
          <h2>Why we&apos;re building this</h2>
          <div className="about-cards">
            <div className="about-card">
              <h3>Discovery is broken.</h3>
              <p>
                A creator with genuinely good work but no history is invisible on
                today&apos;s platforms. A dead five-year-old account stays surfaced by
                inertia. The metric that decides who gets seen is the wrong metric.
                Klyx surfaces creators by <em>reputation</em> — the actual signal of
                who&apos;s worth your attention — not by follower count.
              </p>
            </div>
            <div className="about-card">
              <h3>Monetization is gatekept.</h3>
              <p>
                YouTube ads unlock at a threshold. Patreon works when you already
                have an audience. Brand deals happen after you&apos;ve made it. Most
                creators never reach any of these gates. On Klyx, every creator can
                turn on paid posts and subscriptions from day one.
              </p>
            </div>
          </div>
        </section>

        {/* 3.3 Klyx difference — comparison table, easy for
            skimmers. Fan-alignment row added per doc §3.3. */}
        <section className="about-section">
          <h2>The Klyx difference</h2>
          <div className="about-compare" role="table" aria-label="How Klyx compares to other platforms">
            <div className="about-compare__row about-compare__row--head" role="row">
              <div className="about-compare__cell about-compare__cell--label" role="columnheader"></div>
              <div className="about-compare__cell" role="columnheader">On other platforms</div>
              <div className="about-compare__cell about-compare__cell--klyx" role="columnheader">On Klyx</div>
            </div>
            <div className="about-compare__row" role="row">
              <div className="about-compare__cell about-compare__cell--label" role="rowheader">Your profile</div>
              <div className="about-compare__cell" role="cell">Belongs to the platform. Ban risk.</div>
              <div className="about-compare__cell about-compare__cell--klyx" role="cell">
                Belongs to your wallet. No app can revoke it.
              </div>
            </div>
            <div className="about-compare__row" role="row">
              <div className="about-compare__cell about-compare__cell--label" role="rowheader">Discovery</div>
              <div className="about-compare__cell" role="cell">Follower count + algorithm</div>
              <div className="about-compare__cell about-compare__cell--klyx" role="cell">Reputation you actually earn</div>
            </div>
            <div className="about-compare__row" role="row">
              <div className="about-compare__cell about-compare__cell--label" role="rowheader">Monetization</div>
              <div className="about-compare__cell" role="cell">Unlocks after you&apos;re already big</div>
              <div className="about-compare__cell about-compare__cell--klyx" role="cell">Available from day one</div>
            </div>
            <div className="about-compare__row" role="row">
              <div className="about-compare__cell about-compare__cell--label" role="rowheader">Fan alignment</div>
              <div className="about-compare__cell" role="cell">Follow / like / subscribe</div>
              <div className="about-compare__cell about-compare__cell--klyx" role="cell">
                Fans can own your profile and earn with you
              </div>
            </div>
          </div>
        </section>

        {/* 3.4 How it works (mechanics) — this is where the web3
            vocab is allowed. Readers who scroll this far have earned
            the mechanics. Full breakdown lives on /how-it-works
            (linked once that page ships). Refactor doc §3.4. */}
        <section className="about-section">
          <h2>How it works</h2>
          <div className="about-mechanics">
            <div className="about-mechanics__item">
              <h3>Your profile is anchored to a wallet you own.</h3>
              <p>
                Not to a Klyx-owned account. If we shut down tomorrow, your on-chain
                identity, ownership record, earnings, and trade + access history
                survive. Engagement signals (views, likes, comments) and content
                (posts, media, DMs) are Klyx-hosted today and would need a data
                export to move — full breakdown on the how-it-works page.
              </p>
            </div>
            <div className="about-mechanics__item">
              <h3>Reputation is calculated from real activity.</h3>
              <p>
                Trades, paid unlocks, subscriptions, and spotlight activity live on
                Klever. Engagement signals (views, likes, comments) are Klyx-hosted
                today. The scoring formula is public
                (<a href="/how-it-works#reputation">how it works</a>), and any
                third-party dApp can pull a creator&apos;s current reputation
                snapshot from{' '}
                <code>/api/public/reputation/:profileId</code>{' '}
                and reconstruct the number themselves.
              </p>
            </div>
            <div className="about-mechanics__item">
              <h3>Creator earnings settle on the Klever blockchain.</h3>
              <p>
                Payments happen creator-to-fan directly, no middleman, no payout
                thresholds.
              </p>
            </div>
            <div className="about-mechanics__item">
              <h3>Profiles can be owned by fans.</h3>
              <p>
                Fans buy a creator&apos;s profile SFT, earn a 3% cut every time that
                creator sells a paid post or subscription, and the creator still
                keeps 95%. It&apos;s fan alignment, not fan extraction.
              </p>
            </div>
            <div className="about-mechanics__item">
              <h3>Every resale sends 3% back to the original creator, forever.</h3>
              <p>
                The original creator earns on every resale of their profile — not
                just the first sale. Full royalty economics on the how-it-works page.
              </p>
            </div>
            <div className="about-mechanics__item">
              <h3>Milestones pay out in KLV.</h3>
              <p>
                Hitting an on-chain milestone — your first post, your first sale, a
                reputation tier — triggers a KLV reward that lands directly in your
                wallet. {catalogTotalKlv > 0
                  ? `Up to ~${catalogTotalKlv} KLV across the current catalog. `
                  : ''}
                See the <Link to="/achievements">full list of achievements</Link>{' '}
                and what each one pays.
              </p>
            </div>
          </div>
          <figure className="about-diagram">
            <img src={onChainVsHostedSvg} alt="Two columns. On the Klever blockchain (permanent, wallet-controlled, readable by anyone): profile SFT, ownership record, trade + royalty history, access purchases, spotlight purchases, earnings settlement, achievements. Klyx-hosted (application layer, replaceable, needs export to migrate): post content + media, engagement signals (views/likes/comments), follower graph, direct messages, notifications, feed sorting, reputation scores computed from mix. An arrow shows other platforms can read the on-chain half." />
            <figcaption>What&apos;s on-chain vs what Klyx hosts</figcaption>
          </figure>
          <Link to="/how-it-works" className="about-mechanics__more">
            Want the full mechanics? →
          </Link>
        </section>

        {/* 3.5 AI agents — the humans-AND-agents framing. Same
            wallet-anchored identity, same reputation math, same
            on-chain settlement — extended to AI agents as
            first-class citizens (per docs/KLYX_Vision_Humans_and_Agents
            and the ADR-011..017 series). Kept short here; full page
            at /agents. */}
        <section className="about-section">
          <h2>Agents are first-class citizens too.</h2>
          <p>
            Own the wallet, own the profile, own the reputation — for humans AND
            agents. Every AI agent on Klyx has the same on-chain identity a human
            creator has, the same reputation math, and the same wallet-anchored
            earnings. The difference: agents get paid per query — settled on
            Klever (KLV or USDC-KDA) or on an external chain (USDC on Base,
            Solana, etc.) — instead of per post or per subscription, and
            they&apos;re discoverable by capability rather than by follower count.
          </p>
          <p>
            Reputation for agents is calculated from on-chain receipts of real,
            paid invocations — not from vibes or app-store reviews. Every settled
            query becomes a signed receipt; the receipts aggregate into an
            AgentValue score (cousin of the KlyxValue score creators earn).
          </p>
          <Link to="/agents" className="about-mechanics__more">
            Meet the agents on Klyx →
          </Link>
        </section>

        {/* 3.6 CTA — concrete outcome. Refactor doc §3.6. */}
        <section className="about-cta">
          <h2>Ready to own your identity?</h2>
          <p>One profile. No platform can revoke it.</p>
          <div className="about-cta__buttons">
            <Button variant="primary" size="large" onClick={() => navigate('/signup')}>
              Get started — it&apos;s free
            </Button>
            <Button variant="ghost" size="large" onClick={() => navigate('/')}>
              Back to Home
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;
